'use client';

import { useTranslations } from 'next-intl';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { ActionIcon, getActionColor } from '../utils/action-style';
import { useLogFormatters } from '../utils/use-log-formatters';
import type { AuditLog } from '../types';

interface SystemLogCardProps {
  log: AuditLog;
}

export function SystemLogCard({ log }: SystemLogCardProps) {
  const t = useTranslations('systemLogs');
  const { translateAction, translateSubjectType } = useLogFormatters();

  const date = new Date(log.created_at);
  const formattedDate = date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
  const formattedTime = date.toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit'
  });

  const actionLabel = translateAction(log.action);
  const subjectLabel = log.subject_type
    ? translateSubjectType(log.subject_type)
    : null;

  return (
    <Card className='border-border hover:border-primary/30 transition-colors'>
      <CardContent className='p-6'>
        {/* Top row: action badge + timestamp */}
        <div className='mb-4 flex items-start justify-between'>
          <Badge
            variant='secondary'
            className={cn(
              'gap-1 text-xs font-medium',
              getActionColor(log.action)
            )}
          >
            <ActionIcon action={log.action} className='h-3 w-3' />
            {actionLabel}
          </Badge>
          <span className='text-muted-foreground shrink-0 text-xs'>
            {formattedDate} {formattedTime}
          </span>
        </div>

        {/* Middle: subject info */}
        {subjectLabel && (
          <div className='text-muted-foreground mb-4 text-sm'>
            <p>
              {log.subject_name
                ? `${log.subject_name} (${subjectLabel})`
                : `${subjectLabel}${log.subject_id ? ` #${log.subject_id}` : ''}`}
            </p>
          </div>
        )}

        {/* Bottom: user info */}
        <div className='flex items-center gap-2 border-t pt-3'>
          {log.user ? (
            <>
              <Avatar className='h-7 w-7'>
                {log.user.avatar && (
                  <AvatarImage src={log.user.avatar} alt={log.user.name} />
                )}
                <AvatarFallback className='bg-primary/10 text-primary text-xs font-medium'>
                  {log.user.name.charAt(0).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div className='min-w-0'>
                <p className='truncate text-sm font-medium'>{log.user.name}</p>
                <p className='text-muted-foreground truncate text-xs'>
                  {log.user.email}
                </p>
              </div>
            </>
          ) : (
            <span className='text-muted-foreground text-sm'>
              {t('systemAction')}
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
